import React, {useEffect, useState, useCallback, useMemo, useRef} from 'react';
import {CircularProgress} from '@mui/material';
import PropTypes from 'prop-types';
import {
  DatePickerInput,
  Dropdown,
  InfoLabel,
  TableSkeletonLoader,
  Toggle
} from '@/components/atoms';
import {PageHeader, TextInputField} from '@/components/molecules';
import {FileUploadWithModal, PageContainer} from '@/components/organisms';
import {useDropdown} from '@/hooks/Dropdown-hook';
import {calculateEndDateByDuration} from '@/components/atoms/DatePickerInput/datePickerInput-hook';
import {fetchLinkedSalesTargets} from '@/services/salesTargets';
import {useAppDispatch, useAppSelector} from '@/store';
import {setSalesTargetFormData} from '@/store/slices/salesTargetSlice';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {
  COUNTRY_CODES,
  COUNTRY_CODES_EXCLUDE_TAX_FROM_GOAL
} from '@/utils/constants/globalConstants';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import {FONT_SIZE} from '@/utils/constants/globalStyle';
import {AccordionItemV2, AccordionV2} from '@pepsico-ds/ui';
import {
  TARGET_TYPES,
  TARGET_TYPES_OPTIONS,
  PRODUCT_GROUP_QUANTITY_WARNING,
  ensureCdaSuperTargetOption
} from '@/utils/constants/saleTargetConstants';
import {isCdaSuperTargetType} from '@/utils/salesTargetStepConfig';
import {downloadTemplateFile} from '@/services/downloadTemplateFile';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {
  Constants,
  DATE_VALIDATION_TYPE
} from '@/utils/constants/commonConstants';
import getTargetRules from '@/helpers/targetRulesHelper';
import AvailabilitySection from './AvailabilitySection';
import AdditionalOptionsSection from './AdditionalOptionsSection';
import PointsDeliverySection from './PointsDeliverySection';
import TargetTemplateSection from './TargetTemplateSection';
import {logger} from '@/helpers/logger';

const getValidStartDate = (startDate) => {
  if (!startDate) return null;
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return null;
  return start;
};

const TargetSetUp = ({formData, setFormData, salesTarget}) => {
  const countryCode = useAppSelector(selectCountryCode);
  const [openUpload, setOpenUpload] = useState(false);
  const [targetTypeOptions, setTargetTypeOptions] = useState([]);
  const {dropdownList, isLoading} = useDropdown({
    dropdownType: DROPDOWN_TYPES.SalesTargetType
  });
  const dispatch = useAppDispatch();

  const [linkedSalesTargetsList, setLinkedSalesTargetsList] = useState([]);
  const [isLinkedTargetsLoading, setIsLinkedTargetsLoading] = useState(false);

  const isPublishedSalesTarget = useMemo(() => {
    const status = salesTarget?.status;
    if (!status) return false;
    const normalizedStatus = String(status).toLowerCase();
    return [
      Constants.Published.toLowerCase(),
      Constants.InProgress.toLowerCase(),
      Constants.Draft.toLowerCase()
    ].includes(normalizedStatus);
  }, [salesTarget?.status]);

  const isEarlyPaymentTarget = useMemo(
    () =>
      formData?.targetType === TARGET_TYPES.EarlyPaymentTarget ||
      TARGET_TYPES_OPTIONS[formData?.targetType] ===
        TARGET_TYPES.EarlyPaymentTarget ||
      TARGET_TYPES_OPTIONS[formData?.targetTypeValue] ===
        TARGET_TYPES.EarlyPaymentTarget,
    [formData?.targetType, formData?.targetTypeValue]
  );

  const isDropSizeTarget = useMemo(
    () =>
      formData?.targetType === TARGET_TYPES.DropSizeTarget ||
      TARGET_TYPES_OPTIONS[formData?.targetType] ===
        TARGET_TYPES.DropSizeTarget ||
      TARGET_TYPES_OPTIONS[formData?.targetTypeValue] ===
        TARGET_TYPES.DropSizeTarget,
    [formData?.targetType, formData?.targetTypeValue]
  );

  const isLinkedTargetType = useMemo(
    () => isEarlyPaymentTarget || isDropSizeTarget,
    [isEarlyPaymentTarget, isDropSizeTarget]
  );

  // Determine edit mode flags based on status (AC5)
  const isEditPublishedBeforeStart = useMemo(() => {
    if (!isPublishedSalesTarget) return false;
    const start = getValidStartDate(formData?.startDate);
    if (!start) return false;
    return new Date() < start;
  }, [isPublishedSalesTarget, formData?.startDate]);

  const isActiveSalesTarget = useMemo(() => {
    if (!isPublishedSalesTarget) return false;
    const start = getValidStartDate(formData?.startDate);
    if (!start) return false;
    return new Date() >= start;
  }, [isPublishedSalesTarget, formData?.startDate]);

  // Check if target was originally active when page loaded (based on server data)
  const isOriginallyActiveSalesTarget = useMemo(() => {
    if (!isPublishedSalesTarget) return false;
    const originalStart = getValidStartDate(
      salesTarget?.startDate || salesTarget?.activeStartDate
    );
    if (!originalStart) return false;
    return new Date() >= originalStart;
  }, [
    isPublishedSalesTarget,
    salesTarget?.startDate,
    salesTarget?.activeStartDate
  ]);

  // Calculate field-level edit restrictions (AC5)
  const disableStartDate = useMemo(() => {
    // If originally ACTIVE (start date was in the past when page loaded), disable Start Date
    if (isOriginallyActiveSalesTarget) return true;
    // If editing published before original start, allow Start Date editing
    if (isEditPublishedBeforeStart) return false;
    // Allow editing in all other cases
    return false;
  }, [isOriginallyActiveSalesTarget, isEditPublishedBeforeStart]);

  const disableEndDate = useMemo(() => {
    // If status is ACTIVE, allow End Date editing
    if (isActiveSalesTarget) return false;
    // If editing published before start, allow End Date editing
    if (isEditPublishedBeforeStart) return false;
    // If published and not before start, disable
    if (isPublishedSalesTarget) return true;
    return false;
  }, [isActiveSalesTarget, isEditPublishedBeforeStart, isPublishedSalesTarget]);

  // Determine if Linked Sales Targets dropdown should be disabled (AC6)
  const isInProgressStatus = useMemo(() => {
    const status = salesTarget?.status;
    if (!status) return false;
    return String(status).toLowerCase() === Constants.InProgress.toLowerCase();
  }, [salesTarget?.status]);

  const disableLinkedSalesTargets = useMemo(() => {
    // Allow editing in Draft status (no status means new target)
    if (!salesTarget?.status) return false;
    // Allow editing in Draft status explicitly
    if (
      String(salesTarget?.status).toLowerCase() ===
      Constants.Draft.toLowerCase()
    ) {
      return false;
    }
    // Disable editing in InProgress status
    if (isInProgressStatus) return true;
    // If originally ACTIVE (start date was in the past when page loaded), disable Linked Sales Targets
    if (isOriginallyActiveSalesTarget) return true;
    // Allow editing in all other cases
    return false;
  }, [salesTarget?.status, isInProgressStatus, isOriginallyActiveSalesTarget]);

  // Challenge Transaction ID stays editable while in Draft; only locked once actually Published
  const disableChallengeTransactionId = useMemo(() => {
    const status = salesTarget?.status;
    if (!status) return false;
    return String(status).toLowerCase() !== Constants.Draft.toLowerCase();
  }, [salesTarget?.status]);

  // Fetch Active + Published Sales Targets for "Linked Sales Target" dropdown
  const linkedTargetDates = useMemo(() => {
    const target = formData?.linkedSalesTargets?.[0];
    if (!target)
      return {
        targetDeliveryDate: null,
        minPointsDeliveryDate: null
      };

    const parseDate = (val) => {
      if (!val) return null;
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return null;
      // Normalize to start of day to avoid timezone issues
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const fmt = (d) =>
      d
        ? `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`
        : null;

    const deliveryDate = parseDate(target.targetDeliveryDate);

    // Add 1 day to minDate to disable the targetDeliveryDate itself
    // react-datepicker disables dates BEFORE minDate, not the minDate itself
    const minPointsDeliveryDate = deliveryDate
      ? new Date(deliveryDate.getTime() + 24 * 60 * 60 * 1000)
      : null;

    return {
      targetDeliveryDate: fmt(deliveryDate),
      minPointsDeliveryDate: minPointsDeliveryDate
    };
  }, [formData?.linkedSalesTargets]);

  const hasUpdatedLinkedTarget = useRef(false);

  useEffect(() => {
    if (!isLinkedTargetType) return;
    setIsLinkedTargetsLoading(true);
    const fetchTargets = async () => {
      try {
        const response = await fetchLinkedSalesTargets([]);
        const rawItems = response?.data || [];
        const targets = rawItems.map((t, index) => ({
          id: `linked-${t.id || index}`,
          displayText: t.name || t.salesTargetName || t.targetName || '-',
          isBadge: false,
          value: `linked-${t.id || index}`,
          originalId: t.id,
          activeStartDate: t.startDate || t.activeStartDate,
          activeEndDate: t.endDate || t.activeEndDate,
          pointsDeliveryDate: t.pointsDeliveryDate || null,
          targetDeliveryDate: t.targetDeliveryDate || null,
          name: t.name || t.salesTargetName || t.targetName
        }));
        setLinkedSalesTargetsList(targets);

        // If a linked sales target is already selected in formData, update it with complete data from API
        // Only do this once to avoid infinite loops
        if (
          !hasUpdatedLinkedTarget.current &&
          formData?.linkedSalesTargets?.length > 0
        ) {
          const selectedLinkedTargetId = formData.linkedSalesTargets[0]?.id;
          const matchedTarget = rawItems.find(
            (t) => t.id === selectedLinkedTargetId
          );
          if (matchedTarget) {
            setFormData((prev) => ({
              ...prev,
              linkedSalesTargets: [
                {
                  id: matchedTarget.id,
                  name:
                    matchedTarget.name ||
                    matchedTarget.salesTargetName ||
                    matchedTarget.targetName ||
                    '-',
                  activeStartDate:
                    matchedTarget.startDate || matchedTarget.activeStartDate,
                  activeEndDate:
                    matchedTarget.endDate || matchedTarget.activeEndDate,
                  pointsDeliveryDate: matchedTarget.pointsDeliveryDate || null,
                  targetDeliveryDate: matchedTarget.targetDeliveryDate || null
                }
              ]
            }));
            hasUpdatedLinkedTarget.current = true;
          }
        }
      } catch (error) {
        logger.error('Error fetching linked sales targets:', error);
        setLinkedSalesTargetsList([]);
      } finally {
        setIsLinkedTargetsLoading(false);
      }
    };
    fetchTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinkedTargetType]);

  // Define valid target types for CSV validation
  const validateCsvForTargetTypes = [
    TARGET_TYPES.MainTarget,
    TARGET_TYPES.ProductTarget,
    TARGET_TYPES.MonthlyTargetPrerequistite,
    TARGET_TYPES.ProductTargetPrerequisite,
    TARGET_TYPES.MonthlyTargetLayered,
    TARGET_TYPES.ProductTargetLayered,
    TARGET_TYPES.EarlyPaymentTarget,
    TARGET_TYPES.DropSizeTarget,
    TARGET_TYPES.ProductGroupQuantity
  ];

  // Memoized Derived Values
  const isMainTarget = useMemo(
    () =>
      formData?.targetType === TARGET_TYPES.MainTarget ||
      formData?.targetType === TARGET_TYPES.ProductTarget,
    [formData?.targetType]
  );

  const isRackChallenge = useMemo(
    () => formData?.targetType === TARGET_TYPES.RackChallenge,
    [formData?.targetType]
  );

  const isProductGroupQuantity = useMemo(
    () => formData?.targetType === TARGET_TYPES.ProductGroupQuantity,
    [formData?.targetType]
  );

  const isNewTarget = useMemo(
    () => !salesTarget?.id && !salesTarget?.status,
    [salesTarget?.id, salesTarget?.status]
  );

  const hasTargetType = Boolean(formData?.targetType);

  const showSetupSections = useMemo(
    () => hasTargetType && !(isProductGroupQuantity && isNewTarget),
    [hasTargetType, isProductGroupQuantity, isNewTarget]
  );

  // isLinkedTargetType and isRackChallenge are mutually exclusive
  const isCdaSuperTarget = useMemo(
    () => isCdaSuperTargetType(formData?.targetType),
    [formData?.targetType]
  );

  const isThreeStepTarget = useMemo(
    () => isLinkedTargetType || isRackChallenge || isCdaSuperTarget,
    [isLinkedTargetType, isRackChallenge, isCdaSuperTarget]
  );

  const isTargetMediaFileExist = useMemo(
    () =>
      formData?.targetMediaFile?.length > 0 &&
      formData?.targetMediaFile[0] !== '',
    [formData?.targetMediaFile]
  );

  // Target types that use the single-file (non-layered) upload card
  const regularUploadTargetTypes = useMemo(
    () => [
      undefined,
      '',
      TARGET_TYPES.MainTarget,
      TARGET_TYPES.ProductTarget,
      TARGET_TYPES.MonthlyTargetPrerequistite,
      TARGET_TYPES.ProductTargetPrerequisite,
      TARGET_TYPES.EarlyPaymentTarget,
      TARGET_TYPES.DropSizeTarget,
      TARGET_TYPES.CdaSuperTarget,
      TARGET_TYPES.ProductGroupQuantity
    ],
    []
  );

  const showRegularTargetUpload = useMemo(
    () =>
      !isRackChallenge &&
      !(isProductGroupQuantity && isNewTarget) &&
      regularUploadTargetTypes.includes(formData?.targetType),
    [
      isRackChallenge,
      isProductGroupQuantity,
      isNewTarget,
      regularUploadTargetTypes,
      formData?.targetType
    ]
  );

  const numberOfLevels = useMemo(
    () => [
      {displayText: '1', id: '1', isBadge: false},
      {displayText: '2', id: '2', isBadge: false},
      {displayText: '3', id: '3', isBadge: false}
    ],
    []
  );

  const milestones = useMemo(
    () => [
      {displayText: '2', id: '1', isBadge: false},
      {displayText: '3', id: '2', isBadge: false}
    ],
    []
  );

  const applyEgyptAutoEndDateRule = useCallback(
    (data) => {
      if (countryCode !== COUNTRY_CODES.EGYPT) return;

      const effectiveStartDateStr = data.startDate ?? formData.startDate;
      const effectiveDurationValue =
        data.durationValue ?? formData.durationValue;
      const effectiveTargetType = data.targetType ?? formData.targetType;

      const isMainOrProductTarget =
        effectiveTargetType === TARGET_TYPES.MainTarget ||
        effectiveTargetType === TARGET_TYPES.ProductTarget;

      if (
        !effectiveStartDateStr ||
        !effectiveDurationValue ||
        effectiveDurationValue === 'CUSTOM' ||
        !isMainOrProductTarget
      ) {
        return;
      }

      const [month, day, year] = String(effectiveStartDateStr)
        .split('/')
        .map(Number);
      if (isNaN(month) || isNaN(day) || isNaN(year)) {
        logger.warn(
          'Invalid start date format for calculation:',
          effectiveStartDateStr
        );
        return;
      }

      const startDateObj = new Date(year, month - 1, day);
      startDateObj.setHours(0, 0, 0);

      const newEndDateObj = calculateEndDateByDuration(
        startDateObj,
        effectiveDurationValue
      );
      if (!newEndDateObj) return;

      const formattedEndDate = `${String(newEndDateObj.getMonth() + 1).padStart(2, '0')}/${String(newEndDateObj.getDate()).padStart(2, '0')}/${newEndDateObj.getFullYear()}`;
      data.endDate = formattedEndDate;
      data.endDateValid = false;
    },
    [countryCode, formData]
  );

  const applyEgyptKsaDurationRule = useCallback(
    (data) => {
      if (
        (countryCode === COUNTRY_CODES.EGYPT ||
          countryCode === COUNTRY_CODES.KSA) &&
        data?.durationValue === ''
      ) {
        data.endDate = null;
        data.endDateValid = true;
      }
    },
    [countryCode]
  );

  const applyAwardPointRule = useCallback(
    (data) => {
      if (data?.awardPointsImmedFlag && !isDropSizeTarget) {
        data.pointsDeliveryDate = null;
        data.pointsDeliveryDateValid = null;
      }
    },
    [isDropSizeTarget]
  );

  // Memoized Handlers
  const handleFilterData = useCallback(
    (data) => {
      let changesToApply = {...data};

      applyEgyptKsaDurationRule(changesToApply);
      applyAwardPointRule(changesToApply);
      applyRoundUpRule(changesToApply);
      applyDisplayRules(changesToApply);
      applyEgyptAutoEndDateRule(changesToApply);
      applyTargetDeliveryDate(changesToApply);

      setFormData((prev) => ({...prev, ...changesToApply}));
    },
    [
      setFormData,
      applyEgyptAutoEndDateRule,
      applyEgyptKsaDurationRule,
      applyAwardPointRule
    ]
  );

  const handleResetFormData = useCallback(
    (data) => {
      const isLayeredTarget =
        data?.targetType === TARGET_TYPES.MonthlyTargetLayered ||
        data?.targetType === TARGET_TYPES.ProductTargetLayered;

      const isEarlyPayment =
        data?.targetType === TARGET_TYPES.EarlyPaymentTarget ||
        data?.targetType === TARGET_TYPES.DropSizeTarget;

      const defaultFormData = {
        targetType: data?.targetType,
        targetTypeValue: data?.targetTypeValue,
        targetName: '',
        challengeTransactionId: '',
        challengeTransactionIdValid: undefined,
        activeDisplay: true,
        excludeTaxFromGoal:
          !COUNTRY_CODES_EXCLUDE_TAX_FROM_GOAL.includes(countryCode),
        isNumberOfLevelsSelected: isLayeredTarget,
        isNumberOfMilestonesSelected: isLayeredTarget,
        // Clear all type-specific fields to ensure clean slate when changing target types
        linkedSalesTargets: [],
        pointsDeliveryDate: '',
        pointsDeliveryDateValid: undefined,
        targetDeliveryDate: '',
        targetDeliveryDateValid: undefined,
        offlineOrdersIncluded: isEarlyPayment,
        joinCampaignFlag: false,
        roundUpPointsFlag: false,
        roundOffValue: undefined,
        roundOffValueValid: undefined,
        awardPointsImmedFlag: false,
        numberLevel: '',
        numberMileStone: '',
        targetMediaFile: [],
        pointDisplay: ''
      };

      setFormData(defaultFormData);
      dispatch(setSalesTargetFormData(defaultFormData));
    },
    [countryCode, dispatch, setFormData]
  );

  const handleFileDownload = useCallback(
    async (typeValue, type = TARGET_TYPES.MainTarget) => {
      const {numberLevel, numberMileStone} = formData;

      const body = {
        type: typeValue,
        ...(numberLevel && {levels: numberLevel}),
        ...(numberMileStone && {mileStones: numberMileStone})
      };

      const response = await downloadTemplateFile(body);
      if (!response) return;

      // Use "BoosterPointsTarget_" prefix for DropSizeTarget only
      const isDropSizeTarget =
        type === TARGET_TYPES.DropSizeTarget ||
        TARGET_TYPES_OPTIONS[type] === TARGET_TYPES.DropSizeTarget;
      const prefix = isDropSizeTarget ? 'BoosterPointsTarget_' : '';
      const fileName = `${prefix}${type?.replace(/[-\s]/g, '')}_${getDateWithTimeString()}`;
      downloadCSVFiles(response, fileName);
    },
    [formData]
  );

  const linkedSalesTargetValue = useMemo(
    () =>
      linkedSalesTargetsList.filter((item) =>
        (formData?.linkedSalesTargets || []).some(
          (lt) => lt.id === item?.originalId
        )
      ),
    [linkedSalesTargetsList, formData?.linkedSalesTargets]
  );

  const handleLinkedSalesTargetChange = useCallback(
    (val) => {
      // Ignore non-selection events emitted during search
      const item = val?.[0];
      const currentId = formData?.linkedSalesTargets?.[0]?.id;
      if ((item?.originalId ?? null) === (currentId ?? null)) return;

      const selected = item
        ? [
            {
              id: item.originalId,
              name: item.name || item.displayText,
              activeStartDate: item.activeStartDate,
              activeEndDate: item.activeEndDate,
              pointsDeliveryDate: item.pointsDeliveryDate || null,
              targetDeliveryDate: item.targetDeliveryDate || null
            }
          ]
        : [];
      setFormData((prev) => ({
        ...prev,
        linkedSalesTargets: selected,
        // When linked sales target is selected, set awardPointsImmedFlag to false
        // Points should be awarded later, tied to linked target's schedule
        ...(selected.length > 0 && {awardPointsImmedFlag: false}),
        ...(selected.length === 0 && {
          pointsDeliveryDate: '',
          pointsDeliveryDateValid: undefined
        })
      }));
    },
    [setFormData, formData?.linkedSalesTargets]
  );

  useEffect(() => {
    if (dropdownList?.length > 0) {
      setTargetTypeOptions(ensureCdaSuperTargetOption(dropdownList));
    }
  }, [dropdownList]);

  useEffect(() => {
    if (countryCode) {
      const excludeTaxFromGoal =
        !COUNTRY_CODES_EXCLUDE_TAX_FROM_GOAL.includes(countryCode);
      // set if it's new record
      if (formData.excludeTaxFromGoal === undefined) {
        setFormData((prev) => ({...prev, excludeTaxFromGoal}));
      }
    }
  }, [countryCode, setFormData, formData.excludeTaxFromGoal]);

  // Memoized Function to Determine Point Display
  const showPointDisplay = useCallback((targetType) => {
    return (
      targetType === TARGET_TYPES.ProductTargetLayered ||
      targetType === TARGET_TYPES.MonthlyTargetLayered
    );
  }, []);

  const applyRoundUpRule = (data) => {
    if (
      typeof data?.roundUpPointsFlag !== 'undefined' &&
      !data.roundUpPointsFlag
    ) {
      data.roundOffValue = undefined;
      data.roundOffValueValid = undefined;
    }
  };

  const applyDisplayRules = (data) => {
    if (data?.activeDisplay) {
      data.displayStartDate = null;
      data.displayEndDate = null;
      data.displayStartDateValid = null;
      data.displayEndDateValid = null;
    }
  };

  const applyTargetDeliveryDate = (data) => {
    if (
      'targetDeliveryDate' in data &&
      (data.targetDeliveryDate === null ||
        data.targetDeliveryDate === undefined)
    ) {
      data.targetDeliveryDateValid = undefined;
    }
  };

  // Memoized target type dropdown values for the Dropdown atom
  const targetTypeDropdownValue = useMemo(() => {
    const current = formData?.targetType;
    if (!current) return [];
    const match = targetTypeOptions.find(
      (opt) => opt?.displayText === current || opt?.value === current
    );
    return match ? [match] : [{id: current, displayText: current}];
  }, [formData?.targetType, targetTypeOptions]);

  // Point display dropdown options
  const pointDisplayOptions = useMemo(
    () => [
      {id: 1, displayText: 'Cumulative'},
      {id: 2, displayText: 'Individual'}
    ],
    []
  );

  const pointDisplayDropdownValue = useMemo(() => {
    const current = formData?.pointDisplay;
    if (!current) return [];
    const match = pointDisplayOptions.find(
      (opt) => opt?.displayText === current
    );
    return match ? [match] : [{id: current, displayText: current}];
  }, [formData?.pointDisplay, pointDisplayOptions]);

  const linkedTargetPointsDeliverySection =
    formData?.linkedSalesTargets?.length > 0 || isDropSizeTarget ? (
      <div className='z-25 relative mb-4'>
        <div>
          <div className='display-first-row'>
            <DatePickerInput
              value={formData?.pointsDeliveryDate}
              updateValue={(val, validControl) =>
                handleFilterData({
                  pointsDeliveryDate: val,
                  pointsDeliveryDateValid: validControl
                })
              }
              uiSchema={{
                className: 'box-size-width',
                placeholder: 'Choose date',
                label: 'Points Delivery Date *',
                isDark: true,
                menuPosition: 'top-left',
                required: false,
                dependentDate: [
                  linkedTargetDates.targetDeliveryDate,
                  formData?.endDate
                ],
                minDate: linkedTargetDates.minPointsDeliveryDate,
                validationRules: [
                  {type: 'required', msg: ` is required`},
                  {
                    type: DATE_VALIDATION_TYPE.START_DATE,
                    msg: ` Points Award Date cannot be in the past`,
                    excludeLabel: true
                  },
                  ...(formData?.endDate || salesTarget?.endDate
                    ? [
                        {
                          type: DATE_VALIDATION_TYPE.START_DATE_GREATER,
                          endDate: formData?.endDate || salesTarget?.endDate,
                          msg: isDropSizeTarget
                            ? ' Date must be later than the Active End Date of the Drop Size Target.'
                            : ' Date must be later than the Active End Date of the Early Payment Target.',
                          excludeLabel: true
                        }
                      ]
                    : []),
                  ...(linkedTargetDates.targetDeliveryDate
                    ? [
                        {
                          type: DATE_VALIDATION_TYPE.START_DATE_GREATER,
                          endDate: linkedTargetDates.targetDeliveryDate,
                          msg: ' Must be after the Target Delivery Date of linked Sales Targets.',
                          excludeLabel: true
                        }
                      ]
                    : [])
                ]
              }}
            />
          </div>
        </div>
      </div>
    ) : null;

  const regularTargetDeliverySection = (
    <>
      <hr className='mb-4 border-[color:var(--color-border-divider)]' />
      <div className='z-25 relative mb-4'>
        <div>
          <div className='display-first-row'>
            <DatePickerInput
              value={formData?.targetDeliveryDate}
              updateValue={(val, validControl) =>
                handleFilterData({
                  targetDeliveryDate: val,
                  targetDeliveryDateValid: validControl
                })
              }
              uiSchema={{
                className: 'box-size-width',
                placeholder: 'Choose date',
                label: 'Target Delivery Date *',
                isDark: true,
                menuPosition: 'top-left',
                required: false,
                dependentDate: formData?.endDate,
                validationRules: [
                  {type: 'required', msg: ` is required`},
                  {
                    type: DATE_VALIDATION_TYPE.START_DATE,
                    msg: ` Target delivery date should be after target end date`,
                    excludeLabel: true
                  },
                  {
                    type: DATE_VALIDATION_TYPE.START_DATE_GREATER,
                    endDate: formData?.endDate,
                    msg: ' Target delivery date should be after active end date',
                    excludeLabel: true
                  }
                ]
              }}
            />
          </div>
        </div>
      </div>
      <PointsDeliverySection
        formData={formData}
        isMainTarget={isMainTarget || isRackChallenge}
        onChange={handleFilterData}
        salesTarget={salesTarget}
      />
    </>
  );

  const renderTargetTypeSection = () => {
    return (
      <>
        <div className='flex items-center justify-between'>
          <PageHeader
            fontSize={FONT_SIZE.LG}
            title={'Target Setup'}
            wrapperClassName='mb-5'
          />
        </div>
        <hr className='mb-4 border-[color:var(--color-border-divider)]' />
        <div className='flex flex-row flex-wrap items-start gap-6 pb-4'>
          {isLoading && (
            <TableSkeletonLoader
              rows={2}
              spacing={30}
              data-testid='dropdown-loader'
            />
          )}
          {targetTypeOptions.length > 0 && (
            <>
              <div>
                <Dropdown
                  key='target-type'
                  label='Target Type *'
                  value={targetTypeDropdownValue}
                  updateValue={(val) => {
                    const selected = val?.[0];
                    handleResetFormData({
                      ...formData,
                      targetType: selected?.displayText ?? '',
                      targetTypeValue: selected?.value ?? ''
                    });
                  }}
                  isJsonWrap={false}
                  childList={targetTypeOptions}
                  className='dropdown-width text-sm'
                  isDark={true}
                  placeholder='Select Target Type'
                  isClearable={true}
                  selection='single'
                  size='medium'
                  disabled={isPublishedSalesTarget}
                />
              </div>

              <div className='max-w-[296px]'>
                <TextInputField
                  value={formData?.targetName}
                  updateValue={(val, isValid) => {
                    handleFilterData({
                      targetName: val,
                      targetNameValid: isValid
                    });
                  }}
                  label='Target Name *'
                  schema={{type: 'string'}}
                  uiSchema={{
                    className: 'box-size-width text-sm',
                    isDark: true,
                    placeholder: 'Add Target Name',
                    disabled: salesTarget?.status != null,
                    validationRules: [
                      {type: 'required', msg: ` is required`},
                      {
                        type: 'noSpecialChar',
                        msg: `Please use alphanumeric values to enter a valid name`,
                        excludeLabel: true
                      }
                    ]
                  }}
                  visible={true}
                  disabled={salesTarget?.status != null}
                />
              </div>

              {isRackChallenge && (
                <div className='max-w-[296px]'>
                  <TextInputField
                    value={formData?.challengeTransactionId}
                    updateValue={(val, isValid) => {
                      handleFilterData({
                        challengeTransactionId: val,
                        challengeTransactionIdValid: isValid
                      });
                    }}
                    label='Challenge Transaction ID *'
                    schema={{type: 'string'}}
                    uiSchema={{
                      className: 'box-size-width text-sm',
                      isDark: true,
                      placeholder: 'Challenge Transaction ID',
                      disabled: disableChallengeTransactionId,
                      validationRules: [{type: 'required', msg: ` is required`}]
                    }}
                    visible={true}
                    disabled={disableChallengeTransactionId}
                  />
                </div>
              )}
            </>
          )}

          {isLinkedTargetType && (
            <div className='h-[85px]'>
              {isLinkedTargetsLoading ? (
                <div className='flex items-center pt-6'>
                  <CircularProgress size={24} />
                </div>
              ) : (
                <Dropdown
                  key='linked-sales-target'
                  isJsonWrap={false}
                  value={linkedSalesTargetValue}
                  childList={linkedSalesTargetsList}
                  isClearable={true}
                  className='linked-target-dropdown'
                  isDark={true}
                  label='Linked Sales Target'
                  selection='single'
                  size='medium'
                  placeholder='Select Sales Target'
                  disabled={isLinkedTargetsLoading || disableLinkedSalesTargets}
                  helperText='Only active and published Sales Targets'
                  hideMenuListOnSelection={true}
                  updateValue={handleLinkedSalesTargetChange}
                />
              )}
            </div>
          )}

          {showPointDisplay(formData?.targetType) && (
            <div>
              <Dropdown
                label='Point Display *'
                value={pointDisplayDropdownValue}
                updateValue={(val) => {
                  const selected = val?.[0];
                  handleFilterData({
                    pointDisplay: selected?.displayText ?? '',
                    pointDisplayValue: selected?.value ?? ''
                  });
                }}
                isJsonWrap={false}
                childList={pointDisplayOptions}
                className='box-size-width text-sm'
                isDark={true}
                placeholder='Choose Point Display'
                isClearable={true}
                selection='single'
                size='medium'
              />
            </div>
          )}
        </div>
        {isProductGroupQuantity && isNewTarget && (
          <InfoLabel
            className='mt-6'
            title={
              <span className='flex flex-col'>
                <strong className='font-bold'>
                  {PRODUCT_GROUP_QUANTITY_WARNING.title}
                </strong>
                <span>{PRODUCT_GROUP_QUANTITY_WARNING.description}</span>
              </span>
            }
          />
        )}
      </>
    );
  };

  return (
    <>
      <PageContainer data-testid='target-setup-card'>
        <>
          {renderTargetTypeSection()}

          {isLinkedTargetType && (
            <div className='mb-4'>
              <div className='filters-partners'>
                <div className='additional-first-row'>
                  <Toggle
                    value={!!formData?.offlineOrdersIncluded}
                    updateValue={(val) =>
                      handleFilterData({
                        offlineOrdersIncluded: val
                      })
                    }
                    uiSchema={{
                      label: 'Include Offline Orders?',
                      isDark: true,
                      showCheckedIcon: false
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          {hasTargetType &&
            !isRackChallenge &&
            !(isProductGroupQuantity && isNewTarget) && (
              <TargetTemplateSection
                formData={formData}
                setFormData={setFormData}
                salesTarget={salesTarget}
                showRegularTargetUpload={showRegularTargetUpload}
                isTargetMediaFileExist={isTargetMediaFileExist}
                numberOfLevels={numberOfLevels}
                milestones={milestones}
                handleFileDownload={handleFileDownload}
                setOpenUpload={setOpenUpload}
                cardTitle={
                  isCdaSuperTarget ? 'Upload Target Template' : 'Target'
                }
                uploadButtonTitle={
                  isCdaSuperTarget
                    ? 'Upload Template'
                    : 'Upload Target Template'
                }
                cardClassName={
                  isCdaSuperTarget
                    ? 'mb-6 box-content w-max max-w-full rounded-lg border border-solid border-[color:var(--color-border-divider)] p-4'
                    : undefined
                }
                titleClassName={
                  isCdaSuperTarget ? 'mb-3 text-base font-bold' : undefined
                }
                buttonRowClassName={
                  isCdaSuperTarget ? 'butt-icon flex items-center' : undefined
                }
                uploadButtonClassName={isCdaSuperTarget ? 'ml-2' : undefined}
              />
            )}
        </>
      </PageContainer>
      {showSetupSections && isCdaSuperTarget && (
        <PageContainer data-testid='sales-target-conditions-card'>
          {/* TODO: Sales Target Conditions are still under discussion. */}
          <PageHeader
            fontSize={FONT_SIZE.LG}
            title='Sales Target Conditions'
            wrapperClassName='mb-5'
          />
          <hr className='mb-4 border-[color:var(--color-border-divider)]' />
          <p className='text-sm'>This section is not finalized.</p>
        </PageContainer>
      )}
      {showSetupSections && !isThreeStepTarget && (
        <PageContainer data-testid='additional-options-card'>
          <div className='accord'>
            <AccordionV2
              className='accord'
              onUpdate={function Qa() {}}
              presentation='no-background'
              separator>
              <AccordionItemV2
                expanded
                label='Additional Options'
                onUpdate={function Qa() {}}
                presentation='box-shadow'>
                <hr className='mb-4 border-[color:var(--color-border-divider)]' />
                <AdditionalOptionsSection
                  formData={formData}
                  countryCode={countryCode}
                  onChange={handleFilterData}
                  salesTarget={salesTarget}
                />
              </AccordionItemV2>
            </AccordionV2>
          </div>
        </PageContainer>
      )}
      {showSetupSections && (
        <PageContainer data-testid='availability-card'>
          <PageHeader
            fontSize={FONT_SIZE.LG}
            title='Availability'
            wrapperClassName='mb-5'
          />
          <hr className='mb-4 border-[color:var(--color-border-divider)]' />
          <div className='relative mb-4 mt-2'>
            <AvailabilitySection
              formData={formData}
              countryCode={countryCode}
              onChange={handleFilterData}
              salesTarget={salesTarget}
              disableStartDate={disableStartDate}
              disableEndDate={disableEndDate}
              {...(isLinkedTargetType && {menuPosition: 'top-left'})}
            />
          </div>
          {isLinkedTargetType
            ? linkedTargetPointsDeliverySection
            : regularTargetDeliverySection}
        </PageContainer>
      )}
      <FileUploadWithModal
        openUpload={openUpload}
        setOpenUpload={setOpenUpload}
        setFormData={setFormData}
        formData={formData}
        uploadFor={Constants.SalesTarget}
        fileType='csv'
        isCsvValidationRequired={validateCsvForTargetTypes.includes(
          formData?.targetType
        )} // Check csv file validation
        customRules={getTargetRules({
          targetType: formData?.targetType,
          numberLevel: formData?.numberLevel,
          numberMileStone: formData?.numberMileStone,
          countryCode: countryCode,
          productGtins: salesTarget?.productGtins
        })}
      />
    </>
  );
};

TargetSetUp.propTypes = {
  formData: PropTypes.object,
  setFormData: PropTypes.func.isRequired,
  salesTarget: PropTypes.object
};

export default React.memo(TargetSetUp);
